import styled from "styled-components";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  Heading,
  Input,
  Spinner,
  Text,
  Button as ChakraButton,
  VStack,
  Image as ChakraImage,
  Link,
} from "@chakra-ui/react";
import { useShowToast } from "../../hooks/useShowToast";
import { Show } from "../Show";
import stripeLogo from "../../assets/stripe-logo.png";
import skipgiveIcon from "../../assets/skipgive-icon.png";
import { getPaymentLinks } from "../../api/clientUser/getPaymentLinks";
import { STRIPE_DASHBOARD, STRIPE_PUBLISHABLE_KEY } from "../../constants";
import { useStepper } from "../../hooks/useStepper";
import { patchUpdateClient } from "../../api/clientUser/patchUpdateUser";
import { DragDrop, LogoFile } from "../DragDrop";
import { postUploadFile } from "../../api/clientUser/postUploadFile";
import { postCreateAccountSession } from "../../api/clientUser/postCreateAccountSession";
import { loadConnectAndInitialize } from "@stripe/connect-js";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StepperWrapper = styled.div`
  width: 100%;
  margin-bottom: 2rem;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const ContentCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  padding: 3rem;
  border: 1px solid rgba(51, 156, 255, 0.1);

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const StripeEmbedContainer = styled.div`
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(51, 156, 255, 0.1);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
`;

const StripeConnectWrapper = styled.div`
  width: 100%;

  /* Override Stripe Connect button styles */
  button[type="button"],
  button[type="submit"] {
    background-color: #339cff !important;
    border-color: #339cff !important;
    color: white !important;
    padding: 12px 32px !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    border-radius: 12px !important;
    box-shadow: 0 10px 30px rgba(51, 156, 255, 0.4) !important;
    transition: all 0.3s ease !important;
  }

  button[type="button"]:hover,
  button[type="submit"]:hover {
    background-color: #1a365d !important;
    border-color: #1a365d !important;
    box-shadow: 0 15px 40px rgba(51, 156, 255, 0.5) !important;
    transform: translateY(-2px) !important;
  }
`;

export const ConnectBank = () => {
  const { accessToken, onboardingResults, getOnboardingResults } = useAuth();
  const { showToast } = useShowToast();
  const [enteredWebsite, setEnteredWebsite] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeConnectInstance, setStripeConnectInstance] = useState<any>(null);
  const onboardingContainerRef = useRef<HTMLDivElement>(null);
  const isInitializingRef = useRef(false);

  const isIndividual = onboardingResults.isIndividual;

  const steps = isIndividual
    ? [{ title: "Connect bank", shortTitle: "Bank" }]
    : [
        { title: "Enter website", shortTitle: "Website" },
        { title: "Upload logo", shortTitle: "Logo" },
        { title: "Connect bank", shortTitle: "Bank" },
      ];

  const calculateStepIndex = () => {
    if (isIndividual) {
      return !onboardingResults.charges ? 0 : 1;
    }
    return !onboardingResults.website
      ? 0
      : !onboardingResults.logo
      ? 1
      : !onboardingResults.charges
      ? 2
      : 3;
  };

  const { stepper, setActiveStep, activeStep } = useStepper({
    index: calculateStepIndex(),
    steps,
  });

  useEffect(() => {
    setActiveStep(calculateStepIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingResults]);

  useEffect(() => {
    if (accessToken && !onboardingResults.stripeId) {
      getOnboardingResults(accessToken);
    }

    if (accessToken) getPaymentLinks(accessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Initialize Stripe Connect when stripeId is available AND we're on the bank step
  const bankStep = isIndividual ? 0 : 2;
  useEffect(() => {
    // Only initialize when on the bank connection step
    if (activeStep !== bankStep) return;

    let mounted = true;
    let accountOnboardingComponent: any = null;
    const containerElement = onboardingContainerRef.current;

    const initializeStripeConnect = async () => {
      if (!onboardingResults.stripeId || !accessToken || !containerElement)
        return;

      // Prevent duplicate initialization
      if (stripeConnectInstance || isInitializingRef.current) return;

      isInitializingRef.current = true;

      try {
        // Fetch account session from our API
        const { clientSecret } = await postCreateAccountSession(
          accessToken,
          process.env.REACT_APP_KISS_API_URL as string
        );

        if (!mounted) return;

        // Initialize Stripe Connect
        const instance = await loadConnectAndInitialize({
          publishableKey: STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret: async () => clientSecret,
          appearance: {
            overlays: "dialog",
            variables: {
              colorPrimary: "#339CFF",
              colorBackground: "#ffffff",
              colorText: "#1A202C",
              colorSecondaryText: "#718096",
              colorDanger: "#EF4444",
              colorBorder: "#E2E8F0",
              fontFamily: "system-ui, sans-serif",
              fontSizeBase: "16px",
              spacingUnit: "8px",
              borderRadius: "12px",
              buttonPrimaryColorBackground: "#339CFF",
              buttonPrimaryColorBorder: "#339CFF",
              buttonPrimaryColorText: "#ffffff",
              buttonSecondaryColorBackground: "#F7FAFC",
              buttonSecondaryColorBorder: "#E2E8F0",
              buttonSecondaryColorText: "#1A202C",
              actionPrimaryColorText: "#339CFF",
              formAccentColor: "#339CFF",
              formHighlightColorBorder: "#339CFF",
            },
          },
        });

        if (!mounted || !containerElement) return;

        // Create the account onboarding component using vanilla JS API
        accountOnboardingComponent = instance.create("account-onboarding");
        accountOnboardingComponent.setOnExit(() => {
          console.log("User exited onboarding!");
          if (accessToken) {
            getOnboardingResults(accessToken);
          }
        });

        // Append it to our container - this renders it inline
        containerElement.appendChild(accountOnboardingComponent);

        setStripeConnectInstance(instance);
        isInitializingRef.current = false;
      } catch (error) {
        console.error("Error initializing Stripe Connect:", error);
        showToast("Failed to initialize Stripe Connect", "error");
        isInitializingRef.current = false;
      }
    };

    initializeStripeConnect();

    return () => {
      mounted = false;
      isInitializingRef.current = false;
      // Cleanup: remove the component from DOM if it exists
      if (accountOnboardingComponent && containerElement) {
        try {
          if (containerElement.contains(accountOnboardingComponent)) {
            containerElement.removeChild(accountOnboardingComponent);
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingResults.stripeId, accessToken, activeStep]);

  const handleSaveWebsite = async () => {
    if (accessToken) {
      setIsProcessing(true);
      if (!enteredWebsite.includes("https://")) {
        showToast('Please enter a valid url that includes "https://"', "error");
        setIsProcessing(false);
        return;
      }
      const res = await patchUpdateClient(
        { website: enteredWebsite ?? undefined },
        accessToken
      );
      if (res.id) showToast("Website saved successfully!", "success");

      // Refresh onboarding results to get latest data
      await getOnboardingResults(accessToken);

      setIsProcessing(false);
      setActiveStep(1);
    }
  };

  const handleLogoUpload = async (file: LogoFile) => {
    if (accessToken) {
      setIsProcessing(true);
      const res = await postUploadFile(
        {
          file: file.base64,
          type: "CLIENT_LOGO",
          mimeType: file.type,
          name: file.name,
        },
        accessToken
      );
      if (res.url) showToast("Logo uploaded successfully!", "success");

      // Refresh onboarding results to get latest data including stripeId
      await getOnboardingResults(accessToken);

      setIsProcessing(false);
      setActiveStep(2);
    }
  };

  const needToConnect = (
    <VStack spacing={6} width="100%">
      <VStack spacing={4} width="100%">
        <Logos>
          <ChakraImage
            borderRadius="50%"
            src={stripeLogo}
            alt="Stripe Logo"
            height="3.5rem"
            mr={4}
          />
          <Heading fontSize="xl" color="gray.300" mb={2}>
            {"----"}
          </Heading>
          <ChakraImage
            borderRadius="50%"
            src={skipgiveIcon}
            alt="SkipGive Logo"
            height="5rem"
            ml={4}
          />
        </Logos>

        <Heading textAlign="center" size="xl" color="#1A202C" fontWeight="800">
          Connect Your Bank Account
        </Heading>
        <Text textAlign="center" fontSize="lg" color="gray.600" maxW="600px">
          We use{" "}
          <Link
            href="https://stripe.com/connect"
            target="_blank"
            rel="noopener noreferrer"
            color="#339CFF"
            fontWeight="700"
          >
            Stripe Connect
          </Link>{" "}
          to securely process payments and deposit funds to your bank.
        </Text>
      </VStack>

      <StripeEmbedContainer>
        <StripeConnectWrapper ref={onboardingContainerRef}>
          {/* Loading spinner shown while initializing */}
          {!stripeConnectInstance && (
            <VStack spacing={4} py={8}>
              <Spinner color="#339CFF" size="xl" />
              <Text color="gray.600">Loading Stripe Connect...</Text>
            </VStack>
          )}
          {/* Stripe Connect onboarding component will be appended here via vanilla JS */}
        </StripeConnectWrapper>
      </StripeEmbedContainer>

      <Show when={onboardingResults.details && !onboardingResults.charges}>
        <Text textAlign="center" fontSize="sm" color="red.500" maxW="600px">
          Integration complete but payments are disabled. Log in to your{" "}
          <Link
            color="#339CFF"
            textDecoration="underline"
            href={STRIPE_DASHBOARD}
          >
            Stripe Account
          </Link>{" "}
          and address any issues then refresh this page.
        </Text>
      </Show>
    </VStack>
  );

  const connected = (
    <ContentCard>
      <VStack spacing={4}>
        <Heading textAlign="center" fontSize="6xl">
          ✅
        </Heading>
        <Heading textAlign="center" size="xl" color="#1A202C" fontWeight="800">
          Account Connected Successfully
        </Heading>
        <Text fontSize="lg" color="gray.600" textAlign="center">
          Your account is now fully set up and ready to accept payments!
        </Text>
        <Text fontSize="md" color="gray.600" textAlign="center">
          Questions? Contact us at{" "}
          <Text as="span" color="#339CFF" fontWeight="700">
            hello@skipgive.com
          </Text>
        </Text>
      </VStack>
    </ContentCard>
  );

  const website = (
    <ContentCard>
      <VStack spacing={6} width="100%">
        <Heading textAlign="center" size="xl" color="#1A202C" fontWeight="800">
          Organization Website
        </Heading>
        <Text textAlign="center" fontSize="lg" color="gray.600" mb={2}>
          Enter your organization's main website URL
        </Text>
        <Input
          placeholder="https://www.walkwithsally.org"
          focusBorderColor="#339CFF"
          onChange={(e) => setEnteredWebsite(e.target.value)}
          type="text"
          width={{ base: "100%", md: "70%" }}
          autoFocus
          size="lg"
          borderRadius="12px"
        />
        <Show when={!!enteredWebsite}>
          <VStack spacing={3} width="100%">
            <Text fontSize="md" color="gray.700" fontWeight="600">
              Please verify your link before continuing:
            </Text>
            <Link
              href={enteredWebsite}
              target="_blank"
              rel="noopener noreferrer"
              color="#339CFF"
              fontSize="md"
              fontWeight="600"
              textDecoration="underline"
            >
              Open {enteredWebsite} →
            </Link>
          </VStack>
        </Show>
        <ChakraButton
          size="lg"
          bg="#339CFF"
          color="white"
          px={8}
          py={6}
          borderRadius="12px"
          fontSize="lg"
          fontWeight="700"
          boxShadow="0 10px 30px rgba(51, 156, 255, 0.4)"
          _hover={{
            bg: "#1a365d",
            transform: "translateY(-2px)",
            boxShadow: "0 15px 40px rgba(51, 156, 255, 0.5)",
          }}
          transition="all 0.3s"
          onClick={handleSaveWebsite}
          isLoading={isProcessing}
          isDisabled={!enteredWebsite}
        >
          Save and Continue
        </ChakraButton>
      </VStack>
    </ContentCard>
  );

  const logo = (
    <ContentCard>
      <Show
        when={!isProcessing}
        whenFalseContent={<Spinner color="#339CFF" size="xl" />}
      >
        <VStack spacing={6} width="100%">
          <Heading
            textAlign="center"
            size="xl"
            color="#1A202C"
            fontWeight="800"
          >
            Organization Logo
          </Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center">
            Upload your organization's{" "}
            <Text as="span" textDecoration="underline" fontWeight="600">
              square
            </Text>{" "}
            logo
          </Text>
          <DragDrop onLogoChange={(logo) => handleLogoUpload(logo)} />
        </VStack>
      </Show>
    </ContentCard>
  );

  if (isIndividual) {
    return (
      <Container>
        <StepperWrapper>{stepper}</StepperWrapper>
        <Show when={activeStep === 0}>{needToConnect}</Show>
        <Show when={activeStep === 1}>{connected}</Show>
      </Container>
    );
  }

  return (
    <Container>
      <StepperWrapper>{stepper}</StepperWrapper>
      <Show when={activeStep === 0}>{website}</Show>
      <Show when={activeStep === 1}>{logo}</Show>
      <Show when={activeStep === 2}>{needToConnect}</Show>
      <Show when={activeStep === 3}>{connected}</Show>
    </Container>
  );
};
